import { ContentfulContext } from '@/utils/context';
import dynamic from 'next/dynamic';
import {
  CONTENTFUL_ARTICLES_ID,
  CONTENTFUL_CATEGORIES_ID,
} from '@/utils/constants';
import { createClient } from 'contentful';
import { Seo } from '@/components/Seo';
import { SEO_DATA } from '@/utils/seoConstants/blog-page';
import Breadcrumbs from '@/containers/Blog/components/Breadcrumbs/Breadrumbs';

const NotFound = dynamic(
  () => import(`@/containers/Blog/components/NotFound/NotFound`),
);
const Blog = dynamic(() => import(`@/containers/Blog`));

const perPage = 10;

export interface ArticleType {
  title: string;
  description: string;
  category: any;
  postDate: string;
  author: string;
  preview: any;
  slug: string;
}

interface IBlogPage {
  articles: ArticleType[];
  categories: any;
  total: number;
  activeCategorySlug: string;
}

const client = createClient({
  space: process.env.CONTENTFUL_SPACE || ``,
  accessToken: process.env.CONTENTFUL_ACCESS_TOKEN || ``,
});

const BlogPage = ({
  articles,
  categories,
  total,
  activeCategorySlug,
}: IBlogPage) => {
  const lastPage = Math.ceil(total / perPage);
  const activeCategoryName = categories.find(
    (el: any) => el?.fields?.slug === activeCategorySlug,
  )?.fields?.name;

  return !articles.length || !categories.length ? (
    <NotFound />
  ) : (
    <ContentfulContext.Provider value={client}>
      <Breadcrumbs name={activeCategoryName} id={activeCategorySlug} />
      <Blog
        articles={articles}
        categories={categories}
        lastPage={lastPage}
        activeCategoryName={activeCategoryName}
        activeCategorySlug={activeCategorySlug}
      />
      <Seo {...SEO_DATA} />
    </ContentfulContext.Provider>
  );
};

export async function getServerSideProps(context: any) {
  const { query } = context;
  const page = query.page >= 1 ? query.page : 1;
  const activeCategorySlug = query.category || null;
  const params: any = {
    content_type: CONTENTFUL_ARTICLES_ID,
    limit: perPage,
    skip: perPage * (page - 1),
    select: `fields.title,fields.description,fields.category,fields.postDate,fields.author,fields.preview,fields.slug`,
  };

  if (activeCategorySlug) {
    params[`fields.category.fields.slug`] = activeCategorySlug;
    params[`fields.category.sys.contentType.sys.id`] = CONTENTFUL_CATEGORIES_ID;
  }

  const articles = await client.getEntries(params);

  const categories = await client.getEntries({
    content_type: CONTENTFUL_CATEGORIES_ID,
  });

  return {
    props: {
      articles: articles.items || [],
      categories: categories.items || [],
      total: articles.total,
      activeCategorySlug,
    },
  };
}

export default BlogPage;
